/**
 * Student Portal — PTE Academic section.
 *
 * Shows:
 *  • Winhouse Estimated PTE Practice Score (from most recent completed sim)
 *  • Skill attempts summary (from /api/pte/me/dashboard)
 *  • Resume banner for in-progress simulation
 *  • Score History (recent simulations) with row-level actions:
 *      Resume · View Results · Retake
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { pteApi } from '@/lib/pteApi';
import { Sparkles, ChevronRight, Trophy, Loader2, PlayCircle, RotateCcw, Eye, Clock, AlertCircle, TrendingUp, Target, Award, Type } from 'lucide-react';

const skillLabels = { speaking: 'Speaking', writing: 'Writing', reading: 'Reading', listening: 'Listening' };
const sectionLabels = {
  speaking_writing: 'Speaking & Writing',
  reading: 'Reading',
  listening: 'Listening',
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return '—'; }
};

const fmtDuration = (s) => {
  if (!s || s < 0) return '—';
  const m = Math.floor(s / 60);
  return m < 60 ? `${m} min` : `${Math.floor(m / 60)}h ${m % 60}m`;
};

const StatusChip = ({ status, reviewPending }) => {
  let text = 'Completed';
  let cls = 'bg-emerald-50 text-emerald-800 border-emerald-200';
  if (status === 'in_progress') { text = 'In Progress'; cls = 'bg-sky-50 text-sky-800 border-sky-200'; }
  else if (reviewPending > 0) { text = 'Awaiting Tutor Review'; cls = 'bg-amber-50 text-amber-800 border-amber-200'; }
  return <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${cls}`}>{text}</span>;
};

const TargetAndBest = ({ analytics, onSaved }) => {
  const [target, setTarget] = useState(null);
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(65);
  useEffect(() => {
    (async () => {
      try {
        const r = await pteApi.getTarget();
        setTarget(r.target); if (r.target?.target_score) setVal(r.target.target_score);
      } catch (_) {}
    })();
  }, []);
  const save = async () => {
    const n = Math.max(10, Math.min(90, parseInt(val, 10) || 65));
    try {
      await pteApi.setTarget(n);
      setTarget({ target_score: n });
      setEditing(false);
      onSaved?.();
    } catch (_) {}
  };
  const current = analytics?.personal_best_full_mock ?? analytics?.personal_best_section_simulation ?? null;
  const gap = target?.target_score && current != null ? target.target_score - current : null;
  return (
    <div className="grid md:grid-cols-2 gap-3">
      {/* Target */}
      <div className="rounded-2xl bg-gradient-to-br from-indigo-600 to-purple-700 text-white p-5" data-testid="pte-target-card">
        <div className="flex items-center justify-between mb-1">
          <div className="text-[10px] uppercase tracking-widest text-indigo-200 flex items-center gap-1">
            <Target className="h-3 w-3" /> My PTE target
          </div>
          {!editing && (
            <button onClick={() => setEditing(true)} data-testid="pte-target-edit"
              className="text-[11px] px-2 py-0.5 rounded bg-white/15 hover:bg-white/25">
              {target?.target_score ? 'Change' : 'Set target'}
            </button>
          )}
        </div>
        {editing ? (
          <div className="flex items-end gap-2">
            <div>
              <div className="text-[10px] text-indigo-100 uppercase mb-1">Target (10–90)</div>
              <input type="number" min="10" max="90" value={val}
                onChange={(e) => setVal(e.target.value)} data-testid="pte-target-input"
                className="w-24 px-3 py-2 rounded-lg bg-white text-slate-900 font-bold text-2xl" />
            </div>
            <button onClick={save} data-testid="pte-target-save"
              className="px-4 py-2 rounded-full bg-white text-indigo-700 font-bold text-sm">Save</button>
            <button onClick={() => setEditing(false)}
              className="px-3 py-2 rounded-full bg-white/10 text-white text-xs">Cancel</button>
          </div>
        ) : (
          <div>
            <div className="text-5xl font-black leading-none mt-2" data-testid="pte-target-value">
              {target?.target_score ?? '—'}
            </div>
            <div className="mt-3 text-xs text-indigo-100 flex items-center gap-2">
              <span>Current best:</span>
              <span className="font-bold text-white" data-testid="pte-target-current">
                {current != null ? current : '—'}
              </span>
              {gap != null && gap > 0 && (
                <span className="px-2 py-0.5 rounded-full bg-white/15 text-white text-[11px] font-semibold">
                  Gap {gap} points
                </span>
              )}
              {gap != null && gap <= 0 && (
                <span className="px-2 py-0.5 rounded-full bg-emerald-400 text-black text-[11px] font-semibold">
                  Target reached
                </span>
              )}
            </div>
            <div className="text-[10px] text-indigo-200 mt-3">
              A preparation aid — not a prediction of your official Pearson PTE result.
            </div>
          </div>
        )}
      </div>

      {/* Personal Best */}
      <div className="rounded-2xl bg-white border border-slate-200 p-5" data-testid="pte-personal-best">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold flex items-center gap-1 mb-2">
          <Award className="h-3 w-3" /> Personal best
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="text-[10px] text-slate-500 uppercase tracking-wider">Section simulation</div>
            <div className="text-3xl font-bold text-slate-900" data-testid="pte-pb-simulation">
              {analytics?.personal_best_section_simulation ?? '—'}
            </div>
          </div>
          <div>
            <div className="text-[10px] text-slate-500 uppercase tracking-wider">Full mock</div>
            <div className="text-3xl font-bold text-slate-900" data-testid="pte-pb-mock">
              {analytics?.personal_best_full_mock ?? '—'}
            </div>
          </div>
        </div>
        <div className="text-[11px] text-slate-500 mt-3">
          Section and mock scores are shown separately — they are not directly comparable.
        </div>
      </div>
    </div>
  );
};

export default function StudentPTESection() {
  const [dashboard, setDashboard] = useState(null);
  const [history, setHistory] = useState({ items: [], total: 0 });
  const [mockHistory, setMockHistory] = useState({ items: [], total: 0 });
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [d, h, m, a] = await Promise.all([
        pteApi.myDashboard().catch(() => ({ attempts: [], skills: {}, xp_total: 0 })),
        pteApi.simulationHistory().catch(() => ({ items: [], total: 0 })),
        pteApi.mockHistory().catch(() => ({ items: [], total: 0 })),
        pteApi.analyticsSummary().catch(() => null),
      ]);
      setDashboard(d);
      setHistory(h);
      setMockHistory(m);
      setAnalytics(a);
    } catch (_) {
      setError('We couldn’t load your PTE progress. Please refresh in a moment.');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <section className="rounded-2xl bg-white border border-slate-200 p-5 text-slate-500 text-sm flex items-center gap-2" data-testid="student-pte-section">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading PTE progress…
      </section>
    );
  }
  if (!dashboard) return null;

  const items = history.items || [];
  const inProgress = items.find((it) => it.status === 'in_progress');
  const lastCompleted = items.find((it) => it.status === 'completed' && it.estimated_score != null);
  const latestScore = lastCompleted?.estimated_score;

  return (
    <section className="rounded-2xl bg-white border border-slate-200 p-5 space-y-5" data-testid="student-pte-section">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">PTE Academic</div>
          <div className="text-lg font-semibold text-slate-900">Your PTE preparation</div>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-800 text-xs font-semibold" data-testid="pte-xp-total">
            <Trophy className="h-3.5 w-3.5" /> {dashboard.xp_total || 0} XP
          </div>
          <Link to="/resources/pte-academic" data-testid="pte-open-hub"
            className="px-3 py-1.5 rounded-full bg-slate-900 text-white text-xs font-semibold inline-flex items-center gap-1 hover:bg-slate-800 transition">
            <Sparkles className="h-3.5 w-3.5" /> Open PTE Hub
          </Link>
        </div>
      </div>

      {error && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-amber-800 text-xs flex items-center gap-2" data-testid="pte-load-error">
          <AlertCircle className="h-3.5 w-3.5" /> {error}
        </div>
      )}

      {/* Resume banner */}
      {inProgress && (
        <div className="rounded-2xl bg-gradient-to-r from-sky-600 to-indigo-700 text-white p-4 md:p-5 flex items-center justify-between gap-3 flex-wrap"
          data-testid="pte-resume-banner">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-sky-200">Simulation in progress</div>
            <div className="text-base md:text-lg font-semibold mt-0.5">
              {sectionLabels[inProgress.section] || inProgress.section} · started {fmtDate(inProgress.started_at)}
            </div>
            <div className="text-xs text-sky-100 mt-0.5">Your responses are saved. Pick up where you left off.</div>
          </div>
          <Link to={`/pte/simulation/${inProgress.section}?view=resume`}
            data-testid="pte-resume-cta"
            className="px-5 py-2.5 rounded-full bg-white text-slate-900 text-sm font-bold inline-flex items-center gap-2 hover:bg-emerald-50 transition">
            <PlayCircle className="h-4 w-4" /> Resume simulation
          </Link>
        </div>
      )}

      {/* Estimated score + skill breakdown */}
      <div className="grid md:grid-cols-3 gap-3">
        <div className="rounded-2xl bg-gradient-to-br from-slate-900 to-emerald-900 text-white p-5 md:col-span-1" data-testid="pte-current-score">
          <div className="text-[11px] uppercase tracking-widest text-emerald-300 mb-1">Winhouse Estimated PTE Practice Score</div>
          <div className="text-5xl font-black leading-none mt-1">{latestScore != null ? latestScore : '—'}</div>
          <div className="text-[11px] text-slate-300 mt-3">
            {latestScore != null
              ? 'Latest completed simulation. This is a Winhouse practice estimate and not an official Pearson PTE score.'
              : 'Complete a full simulation to see your estimated practice score.'}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 md:col-span-2">
          {['speaking', 'writing', 'reading', 'listening'].map((s) => {
            const st = (dashboard.skills || {})[s] || { attempts: 0, correct: 0, review: 0 };
            return (
              <div key={s} className="rounded-2xl bg-slate-50 border border-slate-100 p-4" data-testid={`pte-skill-${s}`}>
                <div className="text-[11px] text-slate-500 uppercase tracking-wider">{skillLabels[s]}</div>
                <div className="text-2xl font-bold mt-1">{st.attempts}</div>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  {st.correct} correct{st.review ? ` · ${st.review} awaiting review` : ''}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Score history */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Recent simulations</div>
          <div className="text-[11px] text-slate-400">{items.length} total</div>
        </div>
        {items.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-200 p-6 text-center text-sm text-slate-500" data-testid="pte-empty-history">
            You haven’t started a PTE simulation yet.{' '}
            <Link to="/resources/pte-academic#simulation" className="text-emerald-700 underline">Start one now</Link>.
          </div>
        ) : (
          <div className="divide-y divide-slate-100 border border-slate-100 rounded-2xl overflow-hidden">
            {items.slice(0, 10).map((it) => (
              <div key={it.id} className="grid grid-cols-12 items-center gap-2 px-4 py-3 hover:bg-slate-50" data-testid={`pte-history-row-${it.id}`}>
                <div className="col-span-12 sm:col-span-3">
                  <div className="text-sm font-semibold text-slate-900">{sectionLabels[it.section] || it.section}</div>
                  <div className="text-[11px] text-slate-500 flex items-center gap-1 mt-0.5">
                    <Clock className="h-3 w-3" /> {fmtDate(it.started_at)}
                  </div>
                </div>
                <div className="col-span-6 sm:col-span-2">
                  <StatusChip status={it.status} reviewPending={it.review_pending} />
                  <div className="text-[11px] text-slate-500 mt-1 capitalize">{it.mode || 'simulation'}</div>
                </div>
                <div className="col-span-3 sm:col-span-2 text-center">
                  <div className="text-[10px] text-slate-500 uppercase tracking-wider">Est. Score</div>
                  <div className="text-lg font-bold text-slate-900 leading-none" data-testid={`pte-history-score-${it.id}`}>
                    {it.estimated_score != null ? it.estimated_score : (it.status === 'in_progress' ? '—' : '—')}
                  </div>
                </div>
                <div className="col-span-3 sm:col-span-2 text-center">
                  <div className="text-[10px] text-slate-500 uppercase tracking-wider">Duration</div>
                  <div className="text-sm font-semibold text-slate-800">{fmtDuration(it.duration_seconds)}</div>
                </div>
                <div className="col-span-12 sm:col-span-3 flex items-center justify-end gap-1.5 flex-wrap">
                  {it.status === 'in_progress' ? (
                    <Link to={`/pte/simulation/${it.section}?view=resume`}
                      data-testid={`pte-history-resume-${it.id}`}
                      className="px-3 py-1.5 rounded-full bg-sky-600 hover:bg-sky-700 text-white text-xs font-semibold inline-flex items-center gap-1 transition">
                      <PlayCircle className="h-3.5 w-3.5" /> Resume
                    </Link>
                  ) : (
                    <Link to={`/pte/simulation/${it.section}?view=results&attempt=${it.id}`}
                      data-testid={`pte-history-view-${it.id}`}
                      className="px-3 py-1.5 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold inline-flex items-center gap-1 transition">
                      <Eye className="h-3.5 w-3.5" /> View results
                    </Link>
                  )}
                  {it.status !== 'in_progress' && (
                    <Link to={`/pte/simulation/${it.section}`}
                      data-testid={`pte-history-retake-${it.id}`}
                      className="px-3 py-1.5 rounded-full bg-white border border-slate-200 hover:border-slate-400 text-slate-800 text-xs font-semibold inline-flex items-center gap-1 transition">
                      <RotateCcw className="h-3.5 w-3.5" /> Retake
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="text-[10px] text-slate-400 mt-2">
          Retake creates a fresh attempt — earlier attempts are preserved.
        </div>
      </div>

      {/* Target Score + Personal Best */}
      <TargetAndBest analytics={analytics} onSaved={load} />

      {/* Full Mock history + Personal Best */}
      {mockHistory.items?.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs uppercase tracking-widest text-slate-500 font-bold inline-flex items-center gap-1">
              <Award className="h-3.5 w-3.5" /> Full mocks
            </div>
            <div className="text-[11px] text-slate-400">Personal best: <strong>{analytics?.personal_best_full_mock ?? '—'}</strong></div>
          </div>
          <div className="divide-y divide-slate-100 border border-slate-100 rounded-2xl overflow-hidden">
            {mockHistory.items.slice(0, 5).map((it) => (
              <div key={it.id} className="grid grid-cols-12 items-center gap-2 px-4 py-3 hover:bg-slate-50" data-testid={`pte-mock-row-${it.id}`}>
                <div className="col-span-12 sm:col-span-4">
                  <div className="text-sm font-semibold text-slate-900">{it.variant_label || it.variant}</div>
                  <div className="text-[11px] text-slate-500 flex items-center gap-1 mt-0.5">
                    <Clock className="h-3 w-3" /> {fmtDate(it.started_at)}
                  </div>
                </div>
                <div className="col-span-6 sm:col-span-3">
                  <StatusChip status={it.status} reviewPending={it.review_pending} />
                  <div className="text-[11px] text-slate-500 mt-1">Full mock</div>
                </div>
                <div className="col-span-3 sm:col-span-2 text-center">
                  <div className="text-[10px] text-slate-500 uppercase tracking-wider">Est. Score</div>
                  <div className="text-lg font-bold text-slate-900 leading-none">
                    {it.estimated_score != null ? it.estimated_score : '—'}
                  </div>
                </div>
                <div className="col-span-3 sm:col-span-3 flex items-center justify-end gap-1.5 flex-wrap">
                  {it.status === 'in_progress' ? (
                    <Link to={`/pte/mock/${it.variant}?view=resume`}
                      data-testid={`pte-mock-resume-${it.id}`}
                      className="px-3 py-1.5 rounded-full bg-sky-600 hover:bg-sky-700 text-white text-xs font-semibold inline-flex items-center gap-1 transition">
                      <PlayCircle className="h-3.5 w-3.5" /> Resume
                    </Link>
                  ) : (
                    <Link to={`/pte/mock/${it.variant}?view=results&attempt=${it.id}`}
                      data-testid={`pte-mock-view-${it.id}`}
                      className="px-3 py-1.5 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold inline-flex items-center gap-1 transition">
                      <Eye className="h-3.5 w-3.5" /> View
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Analytics: Recommended + Strengths/Weaknesses */}
      {analytics && (analytics.recommendation || analytics.weakest?.length > 0) && (
        <div className="grid md:grid-cols-3 gap-3">
          {analytics.recommendation && (
            <Link to={analytics.recommendation.route}
              data-testid="pte-recommendation"
              className="rounded-2xl bg-gradient-to-br from-indigo-600 to-blue-700 text-white p-4 flex flex-col justify-between hover:shadow-lg transition">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-indigo-200 inline-flex items-center gap-1 mb-1">
                  <Target className="h-3 w-3" /> Recommended next practice
                </div>
                <div className="text-base font-bold">{analytics.recommendation.label}</div>
                <div className="text-[11px] text-indigo-100 mt-1">{analytics.recommendation.reason}</div>
              </div>
              <div className="mt-3 inline-flex items-center gap-1 text-white text-xs font-semibold">
                Practise now <ChevronRight className="h-3.5 w-3.5" />
              </div>
            </Link>
          )}
          {analytics.strongest?.length > 0 && (
            <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-4" data-testid="pte-strengths">
              <div className="text-[10px] uppercase tracking-widest text-emerald-800 font-bold inline-flex items-center gap-1 mb-1">
                <TrendingUp className="h-3 w-3" /> Strengths
              </div>
              <ul className="space-y-1">
                {analytics.strongest.slice(0, 3).map((s) => (
                  <li key={s.question_type} className="text-xs text-slate-800 flex items-center justify-between">
                    <span>{s.label}</span>
                    <span className="text-emerald-700 font-semibold">{Math.round((s.accuracy || 0) * 100)}%</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {analytics.weakest?.length > 0 && (
            <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4" data-testid="pte-focus-areas">
              <div className="text-[10px] uppercase tracking-widest text-amber-800 font-bold inline-flex items-center gap-1 mb-1">
                <Target className="h-3 w-3" /> Focus areas
              </div>
              <ul className="space-y-1">
                {analytics.weakest.slice(0, 3).map((s) => (
                  <li key={s.question_type} className="text-xs text-slate-800 flex items-center justify-between">
                    <span>{s.label}</span>
                    <span className="text-amber-700 font-semibold">{Math.round((s.accuracy || 0) * 100)}%</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Vocabulary CTA */}
      <Link to="/pte/vocab" data-testid="pte-vocab-link"
        className="block rounded-2xl bg-gradient-to-r from-amber-500 to-orange-600 text-white p-4 hover:shadow-lg transition">
        <div className="flex items-center justify-between">
          <div className="flex items-start gap-3">
            <Type className="h-8 w-8" />
            <div>
              <div className="text-[10px] uppercase tracking-widest text-amber-100">Vocabulary Builder</div>
              <div className="text-base font-bold">Boost your academic vocabulary and earn XP</div>
            </div>
          </div>
          <ChevronRight className="h-5 w-5" />
        </div>
      </Link>

      {/* Skill-level accents (unchanged legacy summary link) */}
      {(dashboard.attempts || []).length > 0 && (
        <details>
          <summary className="text-[11px] uppercase font-semibold tracking-wider text-slate-500 cursor-pointer">
            Practice attempts ({(dashboard.attempts || []).length})
          </summary>
          <div className="mt-2 space-y-1">
            {(dashboard.attempts || []).slice(0, 8).map((a) => (
              <div key={a.id} className="flex items-center justify-between text-xs text-slate-700 py-1 border-t border-slate-100">
                <span>{a.question_type} <span className="text-slate-400">· {a.difficulty}</span></span>
                <span className="text-slate-500 font-mono">{a.score ?? '—'}/{a.max_score ?? '—'} · {fmtDate(a.created_at)}</span>
              </div>
            ))}
          </div>
        </details>
      )}

      <div className="text-[11px] text-slate-500 border-t border-slate-100 pt-3 flex items-start gap-2">
        <ChevronRight className="h-3.5 w-3.5 mt-0.5 shrink-0 text-slate-400" />
        <span>This is a Winhouse practice estimate designed to support preparation and is not an official Pearson PTE score.</span>
      </div>
    </section>
  );
}
